import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Paper,
  alpha,
  styled,
  Alert,
  IconButton,
  Tooltip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { uploadDocument, fetchVerificationStatus } from '../../../features/doctors/onboardingSlice';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textTransform: 'none',
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
  '&:hover': {
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

const DocumentVerification = ({ formData, onChange, errors }) => {
  const dispatch = useDispatch();
  const { verificationStatus, availableOptions, loading } = useSelector((state) => state.onboarding);
  const [uploadProgress, setUploadProgress] = useState({});
  const [localErrors, setLocalErrors] = useState({});

  useEffect(() => {
    dispatch(fetchVerificationStatus());
  }, [dispatch]);

  const getRequiredDocuments = () => {
    const professionalType = formData.professionalType;
    if (!professionalType) return [];

    const requiredDocs = {
      psychiatrist: [
        { id: 'mbbs', label: 'MBBS Degree', required: true },
        { id: 'md_psychiatry', label: 'MD/DPM in Psychiatry', required: true },
        { id: 'mci_registration', label: 'MCI Registration', required: true },
        { id: 'experience', label: 'Experience Certificate', required: true },
        { id: 'identity_proof', label: 'Identity Proof', required: true },
      ],
      clinical_psychologist: [
        { id: 'masters_psychology', label: 'Masters in Psychology', required: true },
        { id: 'mphil_clinical', label: 'MPhil in Clinical Psychology', required: true },
        { id: 'rci_registration', label: 'RCI Registration', required: true },
        { id: 'clinical_training', label: 'Clinical Training Certificate', required: true },
        { id: 'identity_proof', label: 'Identity Proof', required: true },
      ],
      counselor: [
        { id: 'masters_counseling', label: 'Masters in Counseling', required: true },
        { id: 'counseling_training', label: 'Counseling Training Certificate', required: true },
        { id: 'supervised_experience', label: 'Supervised Experience Certificate', required: true },
        { id: 'identity_proof', label: 'Identity Proof', required: true },
      ],
      psychiatric_nurse: [
        { id: 'bsc_nursing', label: 'BSc Nursing', required: true },
        { id: 'psychiatric_nursing', label: 'Psychiatric Nursing Certificate', required: true },
        { id: 'nursing_council_registration', label: 'Nursing Council Registration', required: true },
        { id: 'clinical_experience', label: 'Clinical Experience Certificate', required: true },
        { id: 'identity_proof', label: 'Identity Proof', required: true },
      ],
      psychiatric_social_worker: [
        { id: 'msw', label: 'Master of Social Work', required: true },
        { id: 'mphil_psychiatric_social_work', label: 'MPhil in Psychiatric Social Work', required: true },
        { id: 'professional_registration', label: 'Professional Registration', required: true },
        { id: 'clinical_training', label: 'Clinical Training Certificate', required: true },
        { id: 'identity_proof', label: 'Identity Proof', required: true },
      ],
    };

    return requiredDocs[professionalType] || [];
  };

  const handleProfessionalTypeChange = (event) => {
    const type = event.target.value;
    onChange({ professionalType: type });
    setLocalErrors({});
  };

  const handleFileUpload = async (documentId, file) => {
    if (!file) return;

    // Validate file type and size
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      setLocalErrors(prev => ({
        ...prev,
        [documentId]: 'Only PDF, JPEG, and PNG files are allowed'
      }));
      return;
    }

    if (file.size > maxSize) {
      setLocalErrors(prev => ({
        ...prev,
        [documentId]: 'File size should not exceed 5MB'
      }));
      return;
    }

    try {
      setUploadProgress(prev => ({ ...prev, [documentId]: 0 }));
      
      const result = await dispatch(uploadDocument({
        documentType: documentId,
        file
      })).unwrap();

      onChange({ documents: { ...formData.documents, [documentId]: result.url } });
      setLocalErrors(prev => ({ ...prev, [documentId]: null }));
      
      // Refresh verification status after upload
      dispatch(fetchVerificationStatus());
    } catch (error) {
      setLocalErrors(prev => ({
        ...prev,
        [documentId]: error.message || 'Failed to upload document'
      }));
    } finally {
      setUploadProgress(prev => ({ ...prev, [documentId]: null }));
    }
  };

  const handleFileDelete = (documentId) => {
    onChange({ documents: { ...formData.documents, [documentId]: null } });
    setLocalErrors(prev => ({ ...prev, [documentId]: null }));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Document Verification
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Professional Type
            </Typography>
            <FormControl fullWidth error={!!errors.professionalType}>
              <InputLabel>Select Your Professional Type</InputLabel>
              <Select
                value={formData.professionalType}
                onChange={handleProfessionalTypeChange}
                label="Select Your Professional Type"
                required
              >
                {availableOptions?.professionalTypes?.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.professionalType && (
                <FormHelperText>{errors.professionalType}</FormHelperText>
              )}
            </FormControl>
          </StyledPaper>
        </Grid>

        {formData.professionalType && (
          <Grid item xs={12}>
            <StyledPaper>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Required Documents
              </Typography>
              <List>
                {getRequiredDocuments().map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <ListItemIcon>
                      <DocumentIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.label}
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {formData.documents?.[doc.id] ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Uploaded"
                                color="success"
                                variant="outlined"
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleFileDelete(doc.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <UploadButton
                              component="label"
                              fullWidth
                              startIcon={<UploadIcon />}
                              disabled={loading || uploadProgress[doc.id] !== undefined}
                            >
                              {uploadProgress[doc.id] !== undefined ? (
                                <CircularProgress
                                  size={24}
                                  variant="determinate"
                                  value={uploadProgress[doc.id]}
                                />
                              ) : (
                                'Upload Document'
                              )}
                              <input
                                type="file"
                                hidden
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload(doc.id, e.target.files[0])}
                              />
                            </UploadButton>
                          )}
                          {(errors[doc.id] || localErrors[doc.id]) && (
                            <FormHelperText error>
                              {errors[doc.id] || localErrors[doc.id]}
                            </FormHelperText>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </StyledPaper>
          </Grid>
        )}
      </Grid>

      <Fade in timeout={500}>
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Document verification helps us ensure:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Your professional credentials are valid</li>
              <li>You meet all regulatory requirements</li>
              <li>Patient safety and quality of care</li>
              <li>Compliance with healthcare regulations</li>
            </ul>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Need help with document verification?
            </Typography>
            <Tooltip title="Contact our support team for assistance">
              <IconButton size="small" color="primary">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mt: 3, fontStyle: 'italic' }}
      >
        * All documents should be clear, legible, and in PDF, JPEG, or PNG format. Maximum file size: 5MB.
      </Typography>
    </Box>
  );
};

export default DocumentVerification; 